import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { celestialCreamLow as p } from "@/data/celestialCreamLow";
import { goToShopify } from "@/lib/shopify";

/**
 * Footwear teaser on the cream homepage.
 * Single-product museum frame for the Celestial Cream™ Low.
 */
export const FootwearSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setIsVisible(true)),
      { threshold: 0.1 }
    );
    const section = document.getElementById("footwear-section");
    if (section) observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="footwear-section"
      data-testid="footwear-section"
      className="section-padding bg-[#F4EFE6] relative overflow-hidden border-t border-stone-300/50"
    >
      <div className="container-luxury relative z-10">
        <div
          className={`text-center mb-14 md:mb-20 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-[11px] tracking-[0.45em] uppercase text-stone-500 mb-5">
            UNIXORN — First Footwear Release
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-stone-900">
            CELESTIAL CREAM<sup className="text-base md:text-lg lg:text-xl align-top">™</sup>
          </h2>
          <p className="text-base md:text-lg text-stone-600 tracking-wide max-w-2xl mx-auto italic">
            A new UNIXORN era. Forged in cream.
          </p>
        </div>

        <div
          className={`group cursor-pointer max-w-6xl mx-auto transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "150ms" }}
          onClick={() => navigate("/footwear")}
          data-testid="footwear-teaser-card"
        >
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-14 items-center">
            {/* Hero image — wide aspect, museum-style */}
            <div className="md:col-span-3 relative aspect-[5/4] overflow-hidden bg-[#F5F0E5] shadow-[0_50px_120px_-40px_rgba(120,100,72,0.32)]">
              <img
                src={p.images.main}
                alt={p.name}
                className="w-full h-full object-contain transition-transform duration-[1300ms] ease-out group-hover:scale-[1.03]"
              />
              <div className="absolute top-5 left-5 px-3.5 py-2 bg-[#F4EFE6]/85 backdrop-blur-sm border border-stone-400/40">
                <span className="text-[10px] tracking-[0.3em] uppercase text-stone-700 font-semibold">
                  Inaugural Release
                </span>
              </div>
            </div>

            {/* Copy */}
            <div className="md:col-span-2 text-center md:text-left">
              <p className="text-[11px] tracking-[0.35em] uppercase text-stone-500 mb-4">
                Now Available
              </p>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-stone-900 tracking-tight mb-5 leading-tight">
                {p.name}
              </h3>
              <p className="text-base md:text-lg text-stone-600 italic mb-7">
                &ldquo;{p.tagline}&rdquo;
              </p>
              <p className="text-2xl md:text-3xl text-stone-900 font-semibold mb-9">
                ${p.price}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToShopify(p.id);
                  }}
                  className="px-10 py-6 bg-stone-900 text-[#F4EFE6] hover:bg-stone-800 transition-all duration-500 rounded-none uppercase tracking-[0.3em] font-bold text-sm"
                  data-testid="footwear-teaser-acquire-btn"
                >
                  Acquire
                </Button>
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/footwear");
                  }}
                  className="px-10 py-6 border border-stone-700/40 text-stone-700 hover:bg-stone-900 hover:text-[#F4EFE6] hover:border-stone-900 transition-all duration-300 rounded-none uppercase tracking-[0.3em] text-sm bg-transparent"
                  data-testid="footwear-teaser-explore-btn"
                >
                  Enter The Gallery
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FootwearSection;
